import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { ArrowLeft, ChevronDown, Clock, Download, QrCode, X, Copy, Check, Palette } from 'lucide-react';
import { getMenuById } from '../../services/menuService';
import { useRestaurant } from '../../context/RestaurantContext';
import { downloadQrCard } from '../../lib/qrCard';

// READ-ONLY menu view. It loads the exact same order-ready payload the order
// screen uses (menuService.getMenuById → the location-scoped menu content), so
// what staff see here is precisely what they can order. Nothing on this page
// adds or changes the menu — editing lives on the separate /edit route.
export default function MenuView({ restaurantId: propRestaurantId }) {
  const navigate = useNavigate();
  const { menuId, restaurantId: urlRestaurantId, locationId } = useParams();
  const restaurantId = propRestaurantId || urlRestaurantId;
  const menusBase = `/restaurant/${restaurantId}/locations/${locationId}/menus`;
  const { toast } = useToast();
  const { restaurant, location } = useRestaurant();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  /* Sections fold like a real menu's chapters. Stored as the CLOSED set so
     everything starts open, and collapsing only hides on screen — the print
     stylesheet forces content back for the PDF, which must never depend on
     what happened to be folded when someone pressed Download. */
  const [closedSections, setClosedSections] = useState(() => new Set());
  const toggleSection = (id) => setClosedSections((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const jumpToSection = (id) => {
    setClosedSections((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // After the section is open, so the scroll lands on unfolded content.
    requestAnimationFrame(() => {
      document.getElementById(`menu-sec-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      // Same location-scoped, order-ready fetch the order screen uses. Returns
      // null when the menu has no orderable items at this branch (empty menu)
      // or isn't mapped here — handled as a read-only empty state below.
      const data = await getMenuById(restaurantId, locationId, menuId);
      setMenu(data);
    } catch (error) {
      console.error('Error loading menu:', error);
      toast({
        title: 'Error',
        description: 'Failed to load menu',
        variant: 'destructive',
      });
      setMenu(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, menuId, toast]);

  useEffect(() => {
    if (restaurantId && locationId && menuId) {
      loadMenu();
    }
  }, [restaurantId, locationId, menuId, loadMenu]);

  const showQR = async () => {
    setQrOpen(true);
    if (qr) return;
    setQrLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const origin = window.location.origin;
      const res = await fetch(
        `${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/qr/?base=${encodeURIComponent(origin)}`
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast({
          title: 'Error',
          description: data.error || 'Failed to generate QR',
          variant: 'destructive',
        });
        setQrOpen(false);
      } else {
        setQr(data.data);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Network error while generating QR',
        variant: 'destructive',
      });
      setQrOpen(false);
    } finally {
      setQrLoading(false);
    }
  };

  /* The download leaves as a designed CARD (lib/qrCard) — restaurant, menu
     name, the code on a poster. The bare PNG still saves if composing fails. */
  const downloadQR = async () => {
    if (!qr?.qr_image) return;
    const fileName = `menu-${menu?.name || menuId}-qr.png`;
    try {
      await downloadQrCard({
        qrDataUrl: qr.qr_image,
        title: restaurant?.name || 'Our Restaurant',
        subtitle: menu?.name || 'Our Menu',
        hint: 'Point your phone camera at the code',
        hint2: 'Browse the menu, dish by dish',
        footnote: location?.name || '',
        fileName,
      });
    } catch {
      const a = document.createElement('a');
      a.href = qr.qr_image;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const copyQRLink = async () => {
    if (!qr?.url) return;
    try {
      await navigator.clipboard.writeText(qr.url);
      setQrCopied(true);
      setTimeout(() => setQrCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const downloadPDF = () => {
    // Browser "Save as PDF" via print dialog. The print stylesheet hides
    // the page chrome (sidebar, header, action buttons) and reveals the
    // .menu-print region so the resulting PDF only carries menu content.
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading menu...</div>
    );
  }

  // A null payload means the menu has no orderable sections at this branch (or
  // isn't mapped here). Read-only empty state — no "add" affordance.
  if (!menu) {
    return (
      <div className="p-6 w-full">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(menusBase)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No sections in this menu yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Add sections and dishes from the menu editor to make this menu orderable.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full menu-print-root">
      {/* Print stylesheet — strips page chrome and prints just .menu-print */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .menu-print-root, .menu-print-root * { visibility: visible !important; }
          .menu-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
          .menu-no-print { display: none !important; }
          aside, nav, header, footer { display: none !important; }
          @page { margin: 16mm; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 menu-no-print">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(menusBase)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{menu.name}</h1>
              <span className={`px-2 py-0.5 text-[11px] rounded-full font-medium ${
                menu.is_active
                  ? 'bg-success-soft text-success'
                  : 'bg-secondary text-muted-foreground'
              }`}>
                {menu.is_active ? '● Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
              {menu.follow_location_hours ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Location hours
                </span>
              ) : (menu.active_from || menu.active_until) && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {menu.active_from || '--:--'}–{menu.active_until || '--:--'}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* View is read-only: only non-mutating actions (QR, print). */}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={showQR}>
            <QrCode className="h-4 w-4 mr-2" />
            Show QR
          </Button>
          <Button variant="outline" onClick={downloadPDF}>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {/* Menu Design Factory — pick one of 5 designed print templates */}
          <Button onClick={() => navigate(`${menusBase}/${menuId}/design`)}>
            <Palette className="h-4 w-4 mr-2" />
            Design &amp; Print
          </Button>
        </div>
      </div>

      {/* Print-only title block (visible only in the printed PDF) */}
      <div className="hidden print:block mb-6">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{menu.name}</h1>
      </div>

      {/* QR Modal */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 menu-no-print"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
              onClick={() => setQrOpen(false)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold text-center text-gray-900">
              Menu QR Code
            </h3>
            <p className="text-xs text-center text-gray-500 mt-1 mb-4">
              {menu.name}
            </p>

            <div className="bg-amber-50/40 rounded-lg border border-dashed border-amber-200 min-h-[260px] flex items-center justify-center p-4">
              {qrLoading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto" />
                  <p className="mt-2 text-xs text-gray-500">Generating QR…</p>
                </div>
              ) : qr ? (
                <img
                  src={qr.qr_image}
                  alt={`QR for ${menu.name}`}
                  className="h-56 w-56 object-contain bg-white rounded shadow-sm"
                />
              ) : (
                <p className="text-xs text-gray-500">No QR yet</p>
              )}
            </div>

            {qr && (
              <>
                <div className="text-[11px] text-gray-500 break-all bg-gray-50 rounded p-2 mt-4">
                  {qr.url}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" className="flex-1" onClick={copyQRLink}>
                    {qrCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy link
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" onClick={downloadQR}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Download
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sections — SPLIT PAGE: a sticky chapter index on the left, the menu
          itself on the right, each section folding like a chapter. Rows read
          like a printed card: name … dotted leader … price, small on purpose. */}
      {menu.sections && menu.sections.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)] gap-5 items-start">
          {/* ---- the index (screen only; the PDF prints the menu, not the chrome) ---- */}
          <aside className="menu-no-print lg:sticky lg:top-4 rounded-xl border border-border bg-card p-2">
            <div className="flex items-center justify-between px-2 pt-1 pb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Sections
              </span>
              <span className="text-[11px] text-muted-foreground">
                {menu.sections.length} · {menu.sections.reduce((n, s) => n + (s.items?.length || 0), 0)} dishes
              </span>
            </div>
            <nav className="space-y-0.5">
              {menu.sections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => jumpToSection(s.id)}
                  className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                >
                  <span className="flex-1 truncate">{s.name}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {s.items?.length || 0}
                  </span>
                </button>
              ))}
            </nav>
            <div className="mt-2 flex gap-1 border-t border-border pt-2">
              <button type="button"
                onClick={() => setClosedSections(new Set())}
                className="flex-1 rounded-md px-2 py-1 text-[11px] font-medium text-primary hover:bg-muted">
                Expand all
              </button>
              <button type="button"
                onClick={() => setClosedSections(new Set(menu.sections.map((s) => s.id)))}
                className="flex-1 rounded-md px-2 py-1 text-[11px] font-medium text-primary hover:bg-muted">
                Collapse all
              </button>
            </div>
          </aside>

          {/* ---- the menu ---- */}
          <div className="space-y-3 min-w-0">
            {menu.sections.map((section) => {
              const open = !closedSections.has(section.id);
              const fmt = (p) => {
                const n = parseFloat(p);
                return Number.isFinite(n) ? n.toFixed(2) : '—';
              };
              return (
                <section key={section.id} id={`menu-sec-${section.id}`}
                  className="rounded-xl border border-border bg-card scroll-mt-4">
                  {/* The chapter heading IS the fold toggle. */}
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left"
                  >
                    <ChevronDown
                      className={`menu-no-print h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                        open ? '' : '-rotate-90'}`}
                    />
                    <span className="font-display text-lg font-semibold text-foreground">
                      {section.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      · {section.items?.length || 0} {(section.items?.length || 0) === 1 ? 'dish' : 'dishes'}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {section.follow_location_hours
                        ? 'Location hours'
                        : (section.available_from || section.available_until)
                          ? `${section.available_from || '--:--'}–${section.available_until || '--:--'}`
                          : 'All day'}
                    </span>
                  </button>

                  {/* Folded away on screen only: `print:block` puts every
                      section back for the PDF, whatever was collapsed. */}
                  <div className={`${open ? '' : 'hidden'} print:block px-4 pb-3`}>
                    {section.items && section.items.length > 0 ? (
                      <div className="divide-y divide-border/60 border-t border-border/60">
                        {section.items.map((item) => {
                          const variants = item.variants || [];
                          return (
                            <div key={item.id} className="py-2">
                              <div className="flex items-baseline gap-2">
                                <span className="text-sm font-medium text-foreground">{item.name}</span>
                                {/* The classic dotted leader carries the eye to the price. */}
                                <span className="flex-1 border-b border-dotted border-border/80 translate-y-[-3px]" />
                                {variants.length === 1 ? (
                                  <span className="text-sm font-semibold tabular-nums text-foreground">
                                    {fmt(variants[0].price)}
                                  </span>
                                ) : variants.length === 0 ? (
                                  <span className="text-xs italic text-muted-foreground">no price set</span>
                                ) : null}
                              </div>
                              {item.description && (
                                <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
                                  {item.description}
                                </p>
                              )}
                              {/* Several sizes stay ONE compact line: base · price pairs. */}
                              {variants.length > 1 && (
                                <div className="mt-1 flex flex-wrap gap-x-5 gap-y-0.5 pl-3">
                                  {variants.map((v) => (
                                    <span key={v.id} className="text-xs text-muted-foreground">
                                      {v.name}{' '}
                                      <span className="font-semibold tabular-nums text-foreground">
                                        {fmt(v.price)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="border-t border-border/60 py-3 text-center text-xs text-muted-foreground">
                        No dishes in this section
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No sections in this menu yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
